/**
 * Feature Marketplace — Browse all features, add to favourites, see plan requirements.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import {
  FEATURES, CATEGORIES, PLAN_NAMES, getFeaturesByCategory,
  type Feature, type FeatureCategory,
} from '@/config/features';
import {
  Star, Search, ArrowRight, Sparkles, Lock, ChevronRight,
  MessageSquare, Upload, Library, Users, BarChart3, Link2, Shield,
  ShieldAlert, Calculator, Zap, CalendarDays, FileText, Scale,
  GitCompare, AlertOctagon, CheckCheck, AlertTriangle, FileCheck,
  FileSearch, BookOpen, Building2, Mail, Rocket, HelpCircle,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/shared/PageHeader';
import { cn } from '@/lib/utils';

// Icon map
const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  MessageSquare, Upload, Library, Users, BarChart3, Link2, Shield,
  ShieldAlert, Calculator, Zap, CalendarDays, FileText, Scale,
  GitCompare, AlertOctagon, CheckCheck, AlertTriangle, FileCheck,
  FileSearch, BookOpen, Building2, Mail, Rocket, Sparkles, HelpCircle,
  Lock,
};

function getIcon(name: string) {
  return ICON_MAP[name] || Sparkles;
}

export default function MarketplacePage() {
  const navigate = useNavigate();
  const { hasPlanTier } = useAuthStore();
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState<FeatureCategory | 'all' | 'favourites'>('all');
  const [favourites, setFavourites] = useState<string[]>([]);

  // Load favourites from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('roni-favourites');
    if (saved) setFavourites(JSON.parse(saved));
  }, []);

  const toggleFavourite = (slug: string) => {
    const updated = favourites.includes(slug)
      ? favourites.filter(f => f !== slug)
      : [...favourites, slug];
    setFavourites(updated);
    localStorage.setItem('roni-favourites', JSON.stringify(updated));
    // Notify sidebar to update
    window.dispatchEvent(new Event('favourites-updated'));
  };

  const grouped = getFeaturesByCategory();

  // Filter features
  const filteredFeatures = FEATURES.filter(f => {
    if (search) {
      const q = search.toLowerCase();
      return f.name.toLowerCase().includes(q) || f.description.toLowerCase().includes(q) || f.category.includes(q);
    }
    if (activeCategory === 'favourites') return favourites.includes(f.slug);
    if (activeCategory !== 'all') return f.category === activeCategory;
    return true;
  });

  const favFeatures = FEATURES.filter(f => favourites.includes(f.slug));

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        icon={Sparkles}
        title="Feature Marketplace"
        description="Explore all features, add favourites, and discover what your plan unlocks."
      />


      {/* Search + Category filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search features..."
            className="pl-9"
          />
        </div>
        <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-thin">
          <FilterPill active={activeCategory === 'all'} onClick={() => setActiveCategory('all')} label="All" />
          <FilterPill
            active={activeCategory === 'favourites'}
            onClick={() => setActiveCategory('favourites')}
            label="Favourites"
            icon={<Star className="h-3 w-3" />}
          />
          {Object.entries(CATEGORIES).map(([key, cat]) => (
            <FilterPill
              key={key}
              active={activeCategory === key}
              onClick={() => setActiveCategory(key as FeatureCategory)}
              label={cat.label}
            />
          ))}
        </div>
      </div>

      {/* Feature grid */}
      {activeCategory === 'all' && !search ? (
        // Grouped by category
        Object.entries(CATEGORIES).map(([catKey, cat]) => {
          const catFeatures = grouped[catKey as FeatureCategory] || [];
          if (catFeatures.length === 0) return null;
          const CatIcon = getIcon(cat.icon);
          return (
            <div key={catKey}>
              <div className="flex items-center gap-2 mb-3">
                <CatIcon className="h-4 w-4 text-muted-foreground" />
                <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{cat.label}</h2>
                <span className="text-xs text-muted-foreground/60">({catFeatures.length})</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
                {catFeatures.map(f => (
                  <FeatureCard
                    key={f.slug}
                    feature={f}
                    hasAccess={hasPlanTier(f.minPlan)}
                    isFavourite={favourites.includes(f.slug)}
                    onToggleFavourite={() => toggleFavourite(f.slug)}
                    onNavigate={() => navigate(`/panel/features/${f.slug}`)}
                    onOpen={() => f.route && navigate(f.route)}
                  />
                ))}
              </div>
            </div>
          );
        })
      ) : (
        // Flat filtered list
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filteredFeatures.map(f => (
            <FeatureCard
              key={f.slug}
              feature={f}
              hasAccess={hasPlanTier(f.minPlan)}
              isFavourite={favourites.includes(f.slug)}
              onToggleFavourite={() => toggleFavourite(f.slug)}
              onNavigate={() => navigate(`/panel/features/${f.slug}`)}
              onOpen={() => f.route && navigate(f.route)}
            />
          ))}
          {filteredFeatures.length === 0 && (
            <div className="col-span-full text-center py-12 text-muted-foreground">
              No features found matching "{search || activeCategory}".
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ==================== Sub-Components ====================

function FilterPill({ active, onClick, label, icon }: { active: boolean; onClick: () => void; label: string; icon?: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all border',
        active
          ? 'bg-[#5048e5] text-white border-[#5048e5]'
          : 'bg-background border-border text-muted-foreground hover:bg-muted'
      )}
    >
      {icon && <span className="mr-1 inline-flex">{icon}</span>}
      {label}
    </button>
  );
}

function FeatureCard({ feature, hasAccess, isFavourite, onToggleFavourite, onNavigate, onOpen }: {
  feature: Feature;
  hasAccess: boolean;
  isFavourite: boolean;
  onToggleFavourite: () => void;
  onNavigate: () => void;
  onOpen: () => void;
}) {
  const Icon = getIcon(feature.icon);
  const plan = PLAN_NAMES[feature.minPlan];

  return (
    <div
      className={cn(
        'group relative rounded-xl border p-4 transition-all hover:shadow-md cursor-pointer',
        hasAccess
          ? 'hover:border-[#5048e5]/30 bg-card'
          : 'bg-muted/30 border-dashed'
      )}
      onClick={onNavigate}
    >
      {/* Favourite star - only for features that have a page */}
      {feature.route && <button
        onClick={(e) => { e.stopPropagation(); onToggleFavourite(); }}
        className="absolute top-3 right-3 p-1 rounded-md hover:bg-muted transition"
        title={isFavourite ? 'Remove from sidebar' : 'Add to sidebar'}
      >
        <Star className={cn('h-4 w-4', isFavourite ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/40')} />
      </button>}

      <div className="flex items-start gap-3">
        <div className={cn(
          'h-10 w-10 rounded-lg flex items-center justify-center shrink-0',
          hasAccess ? 'bg-[#5048e5]/10 text-[#5048e5]' : 'bg-muted text-muted-foreground'
        )}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1 pr-6">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-sm font-semibold truncate">{feature.name}</h3>
          </div>
          <Badge className={cn('text-[9px] px-1.5 py-0 mb-2', plan.bg, plan.color, 'border-0')}>
            {plan.name}
          </Badge>
          <p className="text-xs text-muted-foreground line-clamp-2 mb-3">
            {feature.highlight || feature.description}
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between mt-2 pt-2 border-t border-border/50">
        <button
          onClick={(e) => { e.stopPropagation(); onNavigate(); }}
          className="text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-1 transition"
        >
          <HelpCircle className="h-3 w-3" /> Learn more
        </button>
        {hasAccess && feature.route ? (
          <button
            onClick={(e) => { e.stopPropagation(); onOpen(); }}
            className="text-[10px] font-medium text-[#5048e5] hover:text-[#3f38c4] flex items-center gap-1 transition"
          >
            Open <ArrowRight className="h-3 w-3" />
          </button>
        ) : !hasAccess ? (
          <span className="text-[10px] text-muted-foreground flex items-center gap-1">
            <Lock className="h-3 w-3" /> Upgrade to unlock
          </span>
        ) : null}
      </div>
    </div>
  );
}
